import React, { Children } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AlertTriangleIcon, ZapIcon, UsersIcon } from 'lucide-react';
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
      delayChildren: 0.1
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  }
};
export function AssessmentScorePage() {
  return <div className="min-h-screen bg-[#f8f8f8] flex flex-col font-sans" data-id="element-672" data-mp="34irqzog4l2wd">
      {/* Top Section - Deep Purple */}
      <div className="bg-[#1a0647] pt-16 pb-24 px-5 rounded-b-[40px] shadow-lg relative z-10" data-id="element-673" data-mp="39hm5wfaz27b7">
        <motion.div className="max-w-md mx-auto flex flex-col items-center text-center" variants={containerVariants} initial="hidden" animate="visible" data-id="element-674" data-mp="1a5077w4312n7">
          {/* Circular Gauge */}
          <motion.div variants={itemVariants} className="relative w-48 h-48 mb-8" data-id="element-675" data-mp="9h01g1bduc4i">
            {/* Background Circle */}
            <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100" data-id="element-676" data-mp="3vvj8fcy12p0y">
              <circle cx="50" cy="50" r="45" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="8" data-id="element-677" data-mp="3ruqyehhc0g3m" />
              {/* Progress Arc (approx 28.7%) */}
              <motion.circle cx="50" cy="50" r="45" fill="none" stroke="#0EB4CC" strokeWidth="8" strokeLinecap="round" strokeDasharray="282.7" initial={{
              strokeDashoffset: 282.7
            }} animate={{
              strokeDashoffset: 282.7 - 282.7 * 0.287
            }} transition={{
              duration: 1.5,
              ease: 'easeOut',
              delay: 0.5
            }} data-id="element-678" data-mp="je4fteyzl3ar" />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center" data-id="element-679" data-mp="2rkzlxn83qg15">
              <span className="text-5xl font-bold text-white leading-none" data-id="element-680" data-mp="2vmbeo1m9vhdk">
                287
              </span>
              <span className="text-[#0EB4CC] text-sm font-bold mt-1" data-id="element-681" data-mp="2hv6dgv39ah14">
                / 1000
              </span>
            </div>
          </motion.div>

          <motion.h1 variants={itemVariants} className="text-2xl md:text-3xl font-bold text-white mb-3" data-id="element-682" data-mp="27y1zhijkdrve">
            Your Starting Point
          </motion.h1>

          <motion.p variants={itemVariants} className="text-[#d0bdf5] text-base mb-10 max-w-xs" data-id="element-683" data-mp="1yxc58j9yc9js">
            This is where you are today. Caveo will walk you through the actions
            that move this number.
          </motion.p>

          <motion.div variants={itemVariants} className="w-full" data-id="element-684" data-mp="1kchiv9kqck6x">
            <Link to="/dashboard" className="block w-full bg-[#8528e2] text-white font-bold py-4 rounded-xl hover:bg-[#7322c4] transition-colors shadow-lg text-lg no-underline" data-id="element-685" data-mp="1fclps5dkmd65">
              Start Your Action Plan
            </Link>
          </motion.div>
        </motion.div>
      </div>

      {/* Bottom Section - Insights */}
      <div className="flex-1 px-5 pt-12 pb-16 -mt-8 relative z-0" data-id="element-686" data-mp="1yao8k2fizdvi">
        <div className="max-w-4xl mx-auto" data-id="element-687" data-mp="1y81z1x92qoan">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-688" data-mp="2czc2635t9dit">
            {/* Card 1 */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.8,
            duration: 0.5
          }} className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm" data-id="element-689" data-mp="27zpmzo6f9n7d">
              <div className="flex items-center gap-2 mb-4" data-id="element-690" data-mp="k9jgg5bid03a">
                <div className="w-8 h-8 rounded-full bg-red-50 flex items-center justify-center" data-id="element-691" data-mp="p19sqgu09xxc">
                  <AlertTriangleIcon size={16} className="text-red-500" data-id="element-692" data-mp="t2vdeocs7gi3" />
                </div>
                <span className="text-xs font-bold text-red-500 uppercase tracking-wider" data-id="element-693" data-mp="3si065grx3d92">
                  Top Priority
                </span>
              </div>
              <h3 className="font-bold text-[#111111] text-lg mb-2 leading-tight" data-id="element-694" data-mp="1c8rbdjlbn1k">
                Shared passwords across critical accounts.
              </h3>
              <p className="text-sm text-gray-500" data-id="element-695" data-mp="acwz3qie4r60">
                We'll walk you through setting up a password manager step by
                step.
              </p>
            </motion.div>

            {/* Card 2 */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.9,
            duration: 0.5
          }} className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm" data-id="element-696" data-mp="1lq94w6xpnjut">
              <div className="flex items-center gap-2 mb-4" data-id="element-697" data-mp="1i3kpsbirowum">
                <div className="w-8 h-8 rounded-full bg-green-50 flex items-center justify-center" data-id="element-698" data-mp="1d5iyj3b6tr82">
                  <ZapIcon size={16} className="text-green-500" data-id="element-699" data-mp="185d9zw07sku7" />
                </div>
                <span className="text-xs font-bold text-green-600 uppercase tracking-wider" data-id="element-700" data-mp="136jvq1d3spsz">
                  Quick Win
                </span>
              </div>
              <h3 className="font-bold text-[#111111] text-lg mb-2 leading-tight" data-id="element-701" data-mp="z7b535dd181n">
                Enable auto-updates on 3 devices.
              </h3>
              <p className="text-sm text-gray-500" data-id="element-702" data-mp="2tizopkujdysd">
                Takes 2 minutes and closes known security holes instantly.
              </p>
            </motion.div>

            {/* Card 3 */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 1.0,
            duration: 0.5
          }} className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm" data-id="element-703" data-mp="vnkeo2d47acs">
              <div className="flex items-center gap-2 mb-4" data-id="element-704" data-mp="1el3srkefjj62">
                <div className="w-8 h-8 rounded-full bg-[#f0ebf8] flex items-center justify-center" data-id="element-705" data-mp="10mdrdskkuxbw">
                  <UsersIcon size={16} className="text-[#8528e2]" data-id="element-706" data-mp="15l8bcxua6rxl" />
                </div>
                <span className="text-xs font-bold text-[#8528e2] uppercase tracking-wider" data-id="element-707" data-mp="3lcmf6wsydx6h">
                  Household Risk
                </span>
              </div>
              <h3 className="font-bold text-[#111111] text-lg mb-2 leading-tight" data-id="element-708" data-mp="3qd0osja7df6m">
                Your parents are most exposed to financial scams.
              </h3>
              <p className="text-sm text-gray-500" data-id="element-709" data-mp="3dtvcyv8ndx44">
                We'll guide them through recognizing and avoiding the most
                common scams.
              </p>
            </motion.div>
          </div>
        </div>
      </div>
    </div>;
}